import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Button } from '../Common'
import { summarizeChannelForHelper, type ChatSummary } from '../../services/helper/helperChatAiService'
import { getHelperLanguagePreference } from '../../services/helper/languageRegistryService'

type SummaryBubbleProps = {
  workspaceId: string
  channelId: string
  /** Recent messages — sliced + ordered by the caller. */
  messages: Array<{ authorName: string; text: string; timestamp: string | null }>
  /** When true the bubble runs on mount. False shows a manual trigger. */
  auto: boolean
}

type State =
  | { status: 'IDLE' }
  | { status: 'LOADING' }
  | { status: 'READY'; summary: ChatSummary }
  | { status: 'ERROR'; message: string }

export const AISummaryBubble = ({ workspaceId, channelId, messages, auto }: SummaryBubbleProps) => {
  const [state, setState] = useState<State>({ status: 'IDLE' })

  const run = async () => {
    if (messages.length === 0) {
      setState({ status: 'ERROR', message: 'No recent messages to summarize.' })
      return
    }
    setState({ status: 'LOADING' })
    try {
      const summary = await summarizeChannelForHelper({
        workspaceId,
        channelId,
        helperLanguage: getHelperLanguagePreference(),
        messages,
      })
      setState({ status: 'READY', summary })
    } catch (error) {
      setState({ status: 'ERROR', message: error instanceof Error ? error.message : 'Summary failed.' })
    }
  }

  useEffect(() => {
    if (auto && state.status === 'IDLE') void run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, channelId])

  return (
    <motion.section
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 flex flex-col gap-2"
      data-testid="helper-summary-bubble"
    >
      <header className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-widest text-[var(--accent-strong)]">What do I do now?</p>
        <Button
          variant="link"
          size="sm"
          onClick={() => { void run() }}
          disabled={state.status === 'LOADING'}
        >
          {state.status === 'LOADING' ? 'Summarizing…' : 'Refresh'}
        </Button>
      </header>

      {state.status === 'IDLE' && (
        <p className="text-sm text-[var(--muted)]">Tap refresh to summarize recent chat.</p>
      )}
      {state.status === 'LOADING' && (
        <p className="text-sm text-[var(--muted)]">Reading the channel…</p>
      )}
      {state.status === 'ERROR' && (
        <p className="text-sm text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">{state.message}</p>
      )}
      {state.status === 'READY' && (
        <div className="flex flex-col gap-2">
          {state.summary.tldr && (
            <p className="text-sm text-[var(--text-strong)]">{state.summary.tldr}</p>
          )}
          {state.summary.actionItems.length > 0 && (
            <ul className="flex flex-col gap-1 text-sm text-[var(--text)]">
              {state.summary.actionItems.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-[var(--accent)] mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </motion.section>
  )
}
